import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/component/navbar';
import PlanCard from '@/components/PlanCard';
import { useSetupPricing } from '@/data/setupPricing';

// Plan cards (titles, copy, features, order) are admin-editable via
// setupPricing/current.operatePlans — see /admin/setup-pricing → Corporate tab.

// Map a marketing-page plan slug (?plan=…) to this flow's plan id.
const SLUG_TO_PLAN: Record<string, string> = {
  'pulse': 'pulse',
  'total-compliance': 'compliance', 'compliance': 'compliance',
  'business-in-a-box': 'business-in-a-box',
};

export default function OperatePlan() {
  const P = useSetupPricing();
  const [selected, setSelected] = useState<string>(() => {
    const slug = (localStorage.getItem('signup_plan') || '').toLowerCase();
    return SLUG_TO_PLAN[slug] || 'business-in-a-box';
  });
  const [showPopup, setShowPopup] = useState(false);
  const navigate = useNavigate();
  // Accounting (operate) onboarding — keep the active view on accounting so the logo
  // and the post-onboarding redirect land on the accounting dashboard.
  // Also consume the marketing plan pre-selection once (so a later manual change sticks).
  useEffect(() => {
    localStorage.setItem('activeServiceView', 'accounting');
    localStorage.removeItem('signup_plan');
  }, []);

  // If the pre-selected/default id isn't one of the configured cards (an admin removed
  // or renamed it), fall back to the first card so the flow can still be completed.
  useEffect(() => {
    if (P.operatePlans.length && !P.operatePlans.some(p => p.id === selected)) {
      setSelected(P.operatePlans[0]!.id);
    }
  }, [P.operatePlans, selected]);

  useEffect(() => {
    const timer = setTimeout(() => setShowPopup(true), 5000);
    return () => clearTimeout(timer);
  }, []);

  const handleNext = () => {
    localStorage.setItem('operatePlan', selected);
    navigate('/operate-invoice');
  };

  // Picking a plan is not a gate. Skipping stores no plan and leaves the flow
  // unfinished on purpose: the answers already given stay in localStorage, and the
  // dashboard's "Complete your company setup" step leads straight back here.
  const handleSkip = () => navigate('/dashboard');

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      <Navbar hideSignBtn={false} logoTo="/dashboard" />
      <div className="flex-1 px-8 py-10 max-w-6xl mx-auto w-full">
        <h1 className="text-2xl font-bold text-gray-900 mb-10">
          Choose a plan to power up your business
        </h1>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
          {P.operatePlans.map(plan => (
            <PlanCard
              key={plan.id}
              plan={plan}
              price={P.plans[plan.id]}
              selected={selected === plan.id}
              onSelect={() => setSelected(plan.id)}
            />
          ))}
        </div>

        <div className="flex items-center gap-4">
          <button type="button" onClick={() => navigate(-1)} className="px-8 py-3 border-2 border-gray-300 rounded-lg font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors">
            ← Back
          </button>
          <button type="button" onClick={handleNext} className="px-8 py-3 bg-amber-400 text-gray-900 font-semibold rounded-lg hover:bg-amber-500 transition-colors">
            Next
          </button>
          <button
            type="button"
            onClick={handleSkip}
            className="ms-auto px-4 py-3 text-sm font-semibold text-gray-500 underline underline-offset-4 hover:text-gray-800 transition-colors"
          >
            Skip for now
          </button>
        </div>
      </div>

      {showPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.25)' }}>
          <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full mx-4">
            <h2 className="text-xl font-bold text-gray-900 mb-2">Feeling Stuck?</h2>
            <p className="text-sm text-gray-600 mb-6 leading-relaxed">
              Book a free demo call today and let our experts guide you.
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowPopup(false)}
                className="flex-1 px-6 py-3 border-2 border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => setShowPopup(false)}
                className="flex-1 px-6 py-3 bg-amber-400 text-gray-900 font-semibold rounded-lg hover:bg-amber-500 transition-colors"
              >
                Book A Demo
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
